import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createTask,
  getTask,
  getTasks,
  updateTask,
  reorderTasks,
  deleteTask,
  createReminder,
  getReminder,
  getReminders,
  updateReminder,
  deleteReminder,
  createCategory,
  getCategories,
  updateCategory,
  deleteCategory,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from "../../firebase";

export const tasksApi = createApi({
  reducerPath: "tasksApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Task", "Reminder", "Category"],
  endpoints: (builder) => ({
    // ============================================
    // TASKS
    // ============================================
    
    // Get tasks
    getTasks: builder.query({
      async queryFn({ clinicId, scope, therapistId, status, categoryId }) {
        try {
          const tasks = await getTasks(clinicId, { scope, therapistId, status, categoryId });
          return { data: tasks };
        } catch (error) {
          console.error("[CK-7040] Error fetching tasks:", {
            errorCode: "CK-7040",
            errorMessage: error.message,
            clinicId,
            scope,
            therapistId,
            status,
            categoryId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Task", id })),
              { type: "Task", id: "LIST" },
            ]
          : [{ type: "Task", id: "LIST" }],
    }),

    // Get single task
    getTask: builder.query({
      async queryFn({ clinicId, taskId }) {
        try {
          const task = await getTask(clinicId, taskId);
          return { data: task };
        } catch (error) {
          console.error("[CK-7041] Error fetching task:", {
            errorCode: "CK-7041",
            errorMessage: error.message,
            clinicId,
            taskId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { taskId }) => [{ type: "Task", id: taskId }],
    }),

    // Create task
    createTask: builder.mutation({
      async queryFn({ clinicId, taskData, creatorId, creatorName }) {
        try {
          const task = await createTask(clinicId, taskData, creatorId, creatorName);
          
          // Log activity
          logActivity(clinicId, {
            therapistId: creatorId,
            therapistName: creatorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_TASK,
            entityType: ENTITY_TYPES.TASK,
            entityId: task.id,
            entityName: task.title,
            details: {
              scope: taskData.scope,
              status: taskData.status,
            },
            success: true,
          });
          
          return { data: task };
        } catch (error) {
          console.error("[CK-7042] Error creating task:", {
            errorCode: "CK-7042",
            errorMessage: error.message,
            clinicId,
            creatorId,
            scope: taskData?.scope,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          logActivity(clinicId, {
            therapistId: creatorId,
            therapistName: creatorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_TASK,
            entityType: ENTITY_TYPES.TASK,
            entityName: taskData?.title,
            success: false,
            errorMessage: error.message,
          });
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    // Update task
    updateTask: builder.mutation({
      async queryFn({ clinicId, taskId, taskData, editorId, editorName }) {
        try {
          const task = await updateTask(clinicId, taskId, taskData, editorId, editorName);
          
          // Log activity
          logActivity(clinicId, {
            therapistId: editorId,
            therapistName: editorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.UPDATE_TASK,
            entityType: ENTITY_TYPES.TASK,
            entityId: taskId,
            entityName: task.title,
            details: {
              newStatus: taskData.status,
            },
            success: true,
          });
          
          return { data: task };
        } catch (error) {
          console.error("[CK-7043] Error updating task:", {
            errorCode: "CK-7043",
            errorMessage: error.message,
            clinicId,
            taskId,
            editorId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          logActivity(clinicId, {
            therapistId: editorId,
            therapistName: editorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.UPDATE_TASK,
            entityType: ENTITY_TYPES.TASK,
            entityId: taskId,
            success: false,
            errorMessage: error.message,
          });
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { taskId }) => [
        { type: "Task", id: taskId },
        { type: "Task", id: "LIST" },
      ],
    }),

    // Delete task
    deleteTask: builder.mutation({
      async queryFn({ clinicId, taskId, taskTitle, therapistId, therapistName }) {
        try {
          await deleteTask(clinicId, taskId);
          
          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_TASK,
              entityType: ENTITY_TYPES.TASK,
              entityId: taskId,
              entityName: taskTitle || 'לא ידוע',
              success: true,
            });
          }
          
          return { data: taskId };
        } catch (error) {
          console.error("[CK-7044] Error deleting task:", {
            errorCode: "CK-7044",
            errorMessage: error.message,
            clinicId,
            taskId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_TASK,
              entityType: ENTITY_TYPES.TASK,
              entityId: taskId,
              entityName: taskTitle,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    reorderTasks: builder.mutation({
      async queryFn({ clinicId, orderedTaskIds }) {
        try {
          await reorderTasks(clinicId, orderedTaskIds);
          return { data: orderedTaskIds };
        } catch (error) {
          console.error("[CK-7045] Error reordering tasks:", {
            errorCode: "CK-7045",
            errorMessage: error.message,
            clinicId,
            count: orderedTaskIds?.length,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Task", id: "LIST" }],
    }),

    // ============================================
    // REMINDERS
    // ============================================

    // Get reminders
    getReminders: builder.query({
      async queryFn({ clinicId, scope, therapistId, categoryId }) {
        try {
          const reminders = await getReminders(clinicId, { scope, therapistId, categoryId });
          return { data: reminders };
        } catch (error) {
          console.error("[CK-7045] Error fetching reminders:", {
            errorCode: "CK-7045",
            errorMessage: error.message,
            clinicId,
            scope,
            therapistId,
            categoryId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Reminder", id })),
              { type: "Reminder", id: "LIST" },
            ]
          : [{ type: "Reminder", id: "LIST" }],
    }),

    // Get single reminder
    getReminder: builder.query({
      async queryFn({ clinicId, reminderId }) {
        try {
          const reminder = await getReminder(clinicId, reminderId);
          return { data: reminder };
        } catch (error) {
          console.error("[CK-7046] Error fetching reminder:", {
            errorCode: "CK-7046",
            errorMessage: error.message,
            clinicId,
            reminderId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { reminderId }) => [{ type: "Reminder", id: reminderId }],
    }),

    // Create reminder
    createReminder: builder.mutation({
      async queryFn({ clinicId, reminderData, creatorId, creatorName }) {
        try {
          const reminder = await createReminder(clinicId, reminderData, creatorId, creatorName);
          
          // Log activity
          logActivity(clinicId, {
            therapistId: creatorId,
            therapistName: creatorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_REMINDER,
            entityType: ENTITY_TYPES.REMINDER,
            entityId: reminder.id,
            entityName: reminder.title,
            details: {
              scope: reminderData.scope,
            },
            success: true,
          });
          
          return { data: reminder };
        } catch (error) {
          console.error("[CK-7047] Error creating reminder:", {
            errorCode: "CK-7047",
            errorMessage: error.message,
            clinicId,
            creatorId,
            scope: reminderData?.scope,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          logActivity(clinicId, {
            therapistId: creatorId,
            therapistName: creatorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.CREATE_REMINDER,
            entityType: ENTITY_TYPES.REMINDER,
            entityName: reminderData?.title,
            success: false,
            errorMessage: error.message,
          });
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Reminder", id: "LIST" }],
    }),

    // Update reminder
    updateReminder: builder.mutation({
      async queryFn({ clinicId, reminderId, reminderData, editorId, editorName }) {
        try {
          const reminder = await updateReminder(clinicId, reminderId, reminderData, editorId, editorName);
          
          // Log activity
          logActivity(clinicId, {
            therapistId: editorId,
            therapistName: editorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.UPDATE_REMINDER,
            entityType: ENTITY_TYPES.REMINDER,
            entityId: reminderId,
            entityName: reminder.title,
            success: true,
          });
          
          return { data: reminder };
        } catch (error) {
          console.error("[CK-7048] Error updating reminder:", {
            errorCode: "CK-7048",
            errorMessage: error.message,
            clinicId,
            reminderId,
            editorId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          logActivity(clinicId, {
            therapistId: editorId,
            therapistName: editorName || 'לא ידוע',
            action: ACTIVITY_ACTIONS.UPDATE_REMINDER,
            entityType: ENTITY_TYPES.REMINDER,
            entityId: reminderId,
            success: false,
            errorMessage: error.message,
          });
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { reminderId }) => [
        { type: "Reminder", id: reminderId },
        { type: "Reminder", id: "LIST" },
      ],
    }),

    // Delete reminder
    deleteReminder: builder.mutation({
      async queryFn({ clinicId, reminderId, reminderTitle, therapistId, therapistName }) {
        try {
          await deleteReminder(clinicId, reminderId);
          
          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_REMINDER,
              entityType: ENTITY_TYPES.REMINDER,
              entityId: reminderId,
              entityName: reminderTitle || 'לא ידוע',
              success: true,
            });
          }
          
          return { data: reminderId };
        } catch (error) {
          console.error("[CK-7049] Error deleting reminder:", {
            errorCode: "CK-7049",
            errorMessage: error.message,
            clinicId,
            reminderId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_REMINDER,
              entityType: ENTITY_TYPES.REMINDER,
              entityId: reminderId,
              entityName: reminderTitle,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Reminder", id: "LIST" }],
    }),

    // ============================================
    // CATEGORIES
    // ============================================

    // Get categories
    getCategories: builder.query({
      async queryFn({ clinicId, type }) {
        try {
          const categories = await getCategories(clinicId, type);
          return { data: categories };
        } catch (error) {
          console.error("[CK-7050] Error fetching categories:", {
            errorCode: "CK-7050",
            errorMessage: error.message,
            clinicId,
            type,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Category", id })),
              { type: "Category", id: "LIST" },
            ]
          : [{ type: "Category", id: "LIST" }],
    }),

    // Create category
    createCategory: builder.mutation({
      async queryFn({ clinicId, categoryData, therapistId, therapistName }) {
        try {
          const category = await createCategory(clinicId, categoryData);
          
          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.CREATE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityId: category.id,
              entityName: category.name,
              details: {
                type: categoryData.type,
                color: categoryData.color,
              },
              success: true,
            });
          }
          
          return { data: category };
        } catch (error) {
          console.error("[CK-7051] Error creating category:", {
            errorCode: "CK-7051",
            errorMessage: error.message,
            clinicId,
            categoryType: categoryData?.type,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.CREATE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityName: categoryData?.name,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Category", id: "LIST" }],
    }),

    // Update category
    updateCategory: builder.mutation({
      async queryFn({ clinicId, categoryId, categoryData, therapistId, therapistName }) {
        try {
          const category = await updateCategory(clinicId, categoryId, categoryData);
          
          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityId: categoryId,
              entityName: category.name,
              success: true,
            });
          }
          
          return { data: category };
        } catch (error) {
          console.error("[CK-7052] Error updating category:", {
            errorCode: "CK-7052",
            errorMessage: error.message,
            clinicId,
            categoryId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.UPDATE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityId: categoryId,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { categoryId }) => [
        { type: "Category", id: categoryId },
        { type: "Category", id: "LIST" },
      ],
    }),

    // Delete category
    deleteCategory: builder.mutation({
      async queryFn({ clinicId, categoryId, categoryName, therapistId, therapistName }) {
        try {
          await deleteCategory(clinicId, categoryId);
          
          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityId: categoryId,
              entityName: categoryName || 'לא ידוע',
              success: true,
            });
          }
          
          return { data: categoryId };
        } catch (error) {
          console.error("[CK-7053] Error deleting category:", {
            errorCode: "CK-7053",
            errorMessage: error.message,
            clinicId,
            categoryId,
            timestamp: new Date().toISOString(),
          });
          
          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || 'לא ידוע',
              action: ACTIVITY_ACTIONS.DELETE_CATEGORY,
              entityType: ENTITY_TYPES.CATEGORY,
              entityId: categoryId,
              entityName: categoryName,
              success: false,
              errorMessage: error.message,
            });
          }
          
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Category", id: "LIST" }],
    }),
  }),
});

export const {
  // Tasks
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useReorderTasksMutation,
  useDeleteTaskMutation,
  // Reminders
  useGetRemindersQuery,
  useGetReminderQuery,
  useCreateReminderMutation,
  useUpdateReminderMutation,
  useDeleteReminderMutation,
  // Categories
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = tasksApi;

